export const DRAG_MIME = 'application/x-umbrella-contract-ids';

export type Folder = {
  id: string;
  name: string;
  parent_id: string | null;
  contract_count: number;
};

export type FolderNode = Folder & { children: FolderNode[] };

export type FolderChoice =
  | { mode: 'none' }
  | { mode: 'existing'; folderId: string }
  | { mode: 'new'; name: string };

const sortNodes = (nodes: FolderNode[]) => {
  nodes.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
  nodes.forEach((n) => sortNodes(n.children));
};

export const normalizeFolderTree = (
  res: any
): { tree: FolderNode[]; flat: Folder[] } => {
  const raw: any[] = Array.isArray(res)
    ? res
    : (res?.results ?? res?.data ?? res?.tree ?? []);

  const kidsOf = (r: any): any[] =>
    r?.children ?? r?.sub_folders ?? r?.subfolders ?? r?.child_folders ?? [];

  const toNode = (r: any, parentId: string | null): FolderNode => {
    const id = String(r.id);
    return {
      id,
      name: r.name ?? r.folder_name ?? '',
      parent_id: parentId,
      contract_count: r.contract_count ?? r.contracts_count ?? r.count ?? 0,
      children: kidsOf(r).map((c) => toNode(c, id)),
    };
  };

  const looksFlat =
    raw.length > 0 &&
    raw.every((r) => kidsOf(r).length === 0) &&
    raw.some((r) => (r.parent ?? r.parent_id) != null);
  let tree: FolderNode[];
  if (looksFlat) {
    const map = new Map<string, FolderNode>();
    raw.forEach((r) => map.set(String(r.id), toNode(r, null)));
    tree = [];
    raw.forEach((r) => {
      const node = map.get(String(r.id)) as FolderNode;
      const pid = r.parent ?? r.parent_id;
      const parent = pid != null ? map.get(String(pid)) : undefined;
      if (parent) {
        node.parent_id = parent.id;
        parent.children.push(node);
      } else {
        tree.push(node);
      }
    });
  } else {
    tree = raw.map((r) => toNode(r, null));
  }
  sortNodes(tree);
  const flat: Folder[] = [];
  const walk = (nodes: FolderNode[]) =>
    nodes.forEach((n) => {
      flat.push({
        id: n.id,
        name: n.name,
        parent_id: n.parent_id,
        contract_count: n.contract_count,
      });
      walk(n.children);
    });
  walk(tree);
  return { tree, flat };
};

export const folderChoiceToPayload = (choice: FolderChoice) => {
  if (choice.mode === 'existing') return { folder: Number(choice.folderId) };
  if (choice.mode === 'new') return { new_folder_name: choice.name.trim() };
  return { folder: null };
};
